// Image sequences (F-01.12, D-43): PNG / EXR files grouped by their frame number, and the view transform that
// turns scene-linear EXR data into display R'G'B' for comparison with video.

export const SEQUENCE_EXTS = ['.png', '.exr'];

export function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

export function isSequenceImage(name: string): boolean {
  return SEQUENCE_EXTS.includes(extOf(name));
}

export interface SequenceGroup<T> {
  /** Display pattern, e.g. "shot010_####.exr". */
  pattern: string;
  ext: string;
  /** Frames sorted by number. */
  frames: { num: number; item: T }[];
  first: number;
  last: number;
  /** Frame numbers between first and last that have no file (shown as missing frames). */
  missing: number[];
}

const NUMBERED = /^(.*?)(\d+)(\.[^.]+)$/;

/**
 * Groups image files by name pattern (prefix + frame number + extension) and returns the groups, largest first.
 * A file without a number forms a group of its own (a single still).
 */
export function groupSequences<T extends { name: string }>(items: readonly T[]): SequenceGroup<T>[] {
  const groups = new Map<string, { prefix: string; ext: string; pads: Set<number>; frames: { num: number; item: T }[] }>();
  let single = 0;
  for (const it of items) {
    if (!isSequenceImage(it.name)) continue;
    const m = NUMBERED.exec(it.name);
    if (!m) {
      groups.set(`\u0000${single++}`, { prefix: it.name, ext: extOf(it.name), pads: new Set(), frames: [{ num: 0, item: it }] });
      continue;
    }
    const [, prefix, digits, ext] = m;
    const key = `${prefix}|${ext.toLowerCase()}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { prefix, ext: ext.toLowerCase(), pads: new Set(), frames: [] }));
    g.pads.add(digits.length);
    g.frames.push({ num: parseInt(digits, 10), item: it });
  }
  const out: SequenceGroup<T>[] = [];
  for (const g of groups.values()) {
    g.frames.sort((a, b) => a.num - b.num);
    // Duplicate numbers (e.g. 01 and 001) keep the first.
    const frames = g.frames.filter((f, i) => i === 0 || f.num !== g.frames[i - 1].num);
    const first = frames[0].num;
    const last = frames[frames.length - 1].num;
    const have = new Set(frames.map((f) => f.num));
    const missing: number[] = [];
    if (last - first < 1_000_000) for (let n = first; n <= last; n++) if (!have.has(n)) missing.push(n);
    const pad = g.pads.size === 1 ? [...g.pads][0] : 1;
    const pattern = g.pads.size ? `${g.prefix}${'#'.repeat(pad)}${g.ext}` : g.prefix;
    out.push({ pattern, ext: g.ext, frames, first, last, missing });
  }
  return out.sort((a, b) => b.frames.length - a.frames.length);
}

// ---------------------------------------------------------------------------------------------- EXR view transform

export type ExrGamut = 'rec709' | 'acescg' | 'aces2065';
export type ExrTransfer = 'srgb' | 'rec709' | 'gamma24' | 'linear';

/** How scene-linear EXR data is shown (F-01.12): source gamut → Rec.709, exposure, display transfer. */
export interface ExrView {
  gamut: ExrGamut;
  transfer: ExrTransfer;
  /** Exposure in stops, applied in linear light before the transfer. */
  exposure: number;
}

export const DEFAULT_EXR_VIEW: ExrView = { gamut: 'rec709', transfer: 'srgb', exposure: 0 };

/** Linear source RGB → linear Rec.709 RGB (row-major 3×3; Bradford D60→D65 for the ACES gamuts). */
export function gamutToRec709(g: ExrGamut): number[] {
  switch (g) {
    case 'acescg':
      return [1.70505, -0.62179, -0.08326, -0.13026, 1.1408, -0.01055, -0.024, -0.12897, 1.15297];
    case 'aces2065':
      return [2.52169, -1.13413, -0.38756, -0.27648, 1.37272, -0.09624, -0.01538, -0.15298, 1.16835];
    default:
      return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }
}

/** Display encoding of a linear value in [0, 1]. */
export function encodeTransfer(v: number, t: ExrTransfer): number {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  switch (t) {
    case 'srgb':
      return v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    case 'rec709':
      return v < 0.018 ? 4.5 * v : 1.099 * Math.pow(v, 0.45) - 0.099;
    case 'gamma24':
      return Math.pow(v, 1 / 2.4);
    default:
      return v;
  }
}

/**
 * Converts decoded EXR samples (float, `channels` per pixel, bottom-up rows as the decoder delivers them) to
 * display R'G'B' as 16-bit RGBA, top row first.
 */
export function exrToRgba16(src: Float32Array | Uint16Array, width: number, height: number, channels: 1 | 4, view: ExrView, half = false): Uint16Array {
  const out = new Uint16Array(width * height * 4);
  const m = gamutToRec709(view.gamut);
  const gain = Math.pow(2, view.exposure);
  // The transfer is evaluated through a 16-bit lookup of the linear value (plenty for 16-bit output).
  const LUT = 1 << 16;
  const lut = new Uint16Array(LUT);
  for (let i = 0; i < LUT; i++) lut[i] = Math.round(encodeTransfer(i / (LUT - 1), view.transfer) * 65535);
  const enc = (v: number) => (v <= 0 ? 0 : v >= 1 ? 65535 : lut[Math.round(v * (LUT - 1))]);
  const read = half ? (i: number) => halfToFloat((src as Uint16Array)[i]) : (i: number) => src[i];
  for (let y = 0; y < height; y++) {
    const inRow = (height - 1 - y) * width * channels;
    const outRow = y * width * 4;
    for (let x = 0; x < width; x++) {
      const i = inRow + x * channels;
      let r: number, g: number, b: number;
      if (channels === 1) r = g = b = read(i) * gain;
      else {
        const r0 = read(i) * gain;
        const g0 = read(i + 1) * gain;
        const b0 = read(i + 2) * gain;
        r = m[0] * r0 + m[1] * g0 + m[2] * b0;
        g = m[3] * r0 + m[4] * g0 + m[5] * b0;
        b = m[6] * r0 + m[7] * g0 + m[8] * b0;
      }
      const o = outRow + x * 4;
      out[o] = enc(r);
      out[o + 1] = enc(g);
      out[o + 2] = enc(b);
      out[o + 3] = 65535;
    }
  }
  return out;
}

export function halfToFloat(h: number): number {
  const s = h & 0x8000 ? -1 : 1;
  const e = (h >> 10) & 0x1f;
  const f = h & 0x3ff;
  if (e === 0) return s * f * 2 ** -24;
  if (e === 31) return f ? NaN : s * Infinity;
  return s * (1 + f / 1024) * 2 ** (e - 15);
}
